import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  TrendingUp, Building, ChevronDown, Receipt, Eye, DollarSign,
  AlertTriangle, Clock, User, Mail, Phone
} from 'lucide-react';
import { DebouncedInput } from '../shared/DebouncedInputs';

/**
 * Tab "Ingresos Proyectados": agrupación de PSPs por cliente con tabla de cuotas.
 */
const IngresosTab = ({
  busquedaCliente, setBusquedaCliente,
  filtroArea, setFiltroArea,
  areasDisponibles,
  ingresosData,
  loadingIngresos,
  propuestasFiltradas,
  expandedPSP, setExpandedPSP,
  handleViewPSP, handleViewCPS,
}) => (
  <div className='space-y-6'>
    {/* Header con título y filtros */}
    <div className='flex items-center justify-between'>
      <div>
        <h3 className='text-xl font-semibold text-slate-900'>
          Ingresos Proyectados
        </h3>
        <p className='text-sm text-slate-500'>
          Control de facturación y proyección de flujo de caja
        </p>
      </div>
      <div className='flex items-center gap-4'>
        <div>
          <Label className='text-xs text-slate-500 mb-1 block'>Buscar Cliente</Label>
          <div className='relative'>
            <DebouncedInput
              type='text'
              placeholder='Nombre o RUT del cliente...'
              value={busquedaCliente}
              onChange={(e) => setBusquedaCliente(e.target.value)}
              className='w-64 pl-8 h-9 text-sm'
              data-testid='busqueda-cliente-ingresos'
            />
            <Building className='w-4 h-4 text-slate-400 absolute left-2.5 top-2.5' />
          </div>
        </div>
        <div>
          <Label className='text-xs text-slate-500 mb-1 block'>Filtrar por Área</Label>
          <Select value={filtroArea} onValueChange={setFiltroArea}>
            <SelectTrigger className='w-48' data-testid='filtro-area-ingresos'>
              <SelectValue placeholder='Todas las áreas' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='todas'>Todas las áreas</SelectItem>
              {areasDisponibles.map(area => (
                <SelectItem key={area} value={area}>{area}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>

    {/* Resumen de Proyección Mensual */}
    {ingresosData.proyeccion_mensual.length > 0 && (
      <Card className='p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'>
        <h4 className='text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2'>
          <TrendingUp className='w-4 h-4 text-blue-600' />
          Proyección de Ingresos - Próximos 6 Meses
        </h4>
        <div className='grid grid-cols-6 gap-2'>
          {ingresosData.proyeccion_mensual.map((mes) => (
            <div key={mes.mes_key} className='bg-white rounded-lg p-3 shadow-sm'>
              <p className='text-xs text-slate-500 font-medium'>{mes.mes_nombre}</p>
              <p className='text-lg font-bold text-slate-900'>{mes.total_uf.toFixed(1)} UF</p>
              <div className='flex gap-1 mt-1'>
                <span className='text-[10px] px-1 py-0.5 bg-orange-100 text-orange-700 rounded'>
                  {mes.pendiente_uf.toFixed(1)} pend
                </span>
                <span className='text-[10px] px-1 py-0.5 bg-blue-100 text-blue-700 rounded'>
                  {mes.facturado_uf.toFixed(1)} fact
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className='mt-3 pt-3 border-t border-blue-200 flex justify-between text-sm'>
          <span className='text-slate-600'>
            Total Pendiente: <strong className='text-orange-600'>{ingresosData.totales.total_pendiente_uf?.toFixed(2)} UF</strong>
          </span>
          <span className='text-slate-600'>
            Total Facturado: <strong className='text-blue-600'>{ingresosData.totales.total_facturado_uf?.toFixed(2)} UF</strong>
          </span>
          <span className='text-slate-600'>
            Total Pagado: <strong className='text-green-600'>{ingresosData.totales.total_pagado_uf?.toFixed(2)} UF</strong>
          </span>
        </div>
      </Card>
    )}

    {/* Lista de PSPs con cuotas */}
    {loadingIngresos ? (
      <Card className='p-12 text-center border-slate-200'>
        <div className='animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2'></div>
        <p className='text-slate-600'>Cargando ingresos...</p>
      </Card>
    ) : propuestasFiltradas.length === 0 ? (
      <Card className='p-12 text-center border-slate-200 border-dashed'>
        <DollarSign className='w-12 h-12 text-slate-300 mx-auto mb-3' />
        <p className='text-slate-600 font-medium'>No hay PSP aceptadas</p>
        <p className='text-sm text-slate-400'>Las propuestas aceptadas aparecerán aquí para su facturación</p>
      </Card>
    ) : (
      <div className='space-y-6'>
        {(() => {
          const clientesAgrupados = {};
          propuestasFiltradas.forEach(psp => {
            const clienteKey = psp.datos_facturacion?.rut || psp.client?.rut || psp.client_rut || psp.client?.name || 'sin-identificar';
            const clienteName = psp.datos_facturacion?.nombre_razon_social || psp.client?.company || psp.client_company || psp.client?.name || psp.client_name || 'Cliente sin nombre';
            if (!clientesAgrupados[clienteKey]) {
              clientesAgrupados[clienteKey] = {
                nombre: clienteName,
                rut: psp.datos_facturacion?.rut || psp.client?.rut || psp.client_rut,
                origen: psp.client?.origen || psp.client_origen,
                propuestas: []
              };
            }
            clientesAgrupados[clienteKey].propuestas.push(psp);
          });

          return Object.entries(clientesAgrupados).map(([clienteKey, clienteData]) => (
            <Card key={clienteKey} className='border-slate-200 overflow-hidden'>
              {/* Header del Cliente */}
              <div className='p-4 bg-gradient-to-r from-slate-800 to-slate-700 border-b border-slate-600'>
                <div className='flex items-start gap-3'>
                  <Building className='w-6 h-6 text-blue-300 mt-1' />
                  <div className='flex-1'>
                    <div className='flex items-center justify-between'>
                      <div>
                        <h3 className='text-xl font-bold text-white'>{clienteData.nombre}</h3>
                        <div className='flex items-center gap-3 mt-1'>
                          <span className='font-mono text-sm text-slate-300'>
                            RUT: {clienteData.rut || 'N/A'}
                          </span>
                          {clienteData.origen && (
                            <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-500/30 text-cyan-300'>
                              Origen: {clienteData.origen}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className='text-right'>
                        <p className='text-lg font-bold text-white'>
                          {clienteData.propuestas.reduce((sum, p) => sum + (p.precio_uf || 0), 0).toFixed(2)} UF
                        </p>
                        <p className='text-xs text-slate-300'>
                          {clienteData.propuestas.length} contrato{clienteData.propuestas.length > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    {clienteData.propuestas[0]?.datos_facturacion?.contacto_nombre && (
                      <div className='mt-2 pt-2 border-t border-slate-600 flex items-center gap-4 text-sm text-slate-300'>
                        <span className='flex items-center gap-1'>
                          <User className='w-3 h-3' />
                          {clienteData.propuestas[0].datos_facturacion.contacto_nombre}
                        </span>
                        <span className='flex items-center gap-1'>
                          <Mail className='w-3 h-3' />
                          {clienteData.propuestas[0].datos_facturacion.contacto_email}
                        </span>
                        <span className='flex items-center gap-1'>
                          <Phone className='w-3 h-3' />
                          {clienteData.propuestas[0].datos_facturacion.contacto_telefono}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className='divide-y divide-slate-200'>
                {clienteData.propuestas.map((psp) => (
                  <div key={psp.id} className='bg-white' data-testid={`ingreso-psp-${psp.id}`}>
                    <div
                      className='flex items-center justify-between cursor-pointer hover:bg-slate-100 p-4 bg-slate-50 border-b border-slate-200'
                      onClick={() => setExpandedPSP(expandedPSP === psp.id ? null : psp.id)}
                    >
                      <div className='flex items-center gap-3'>
                        <span className='font-mono text-sm bg-primary/10 px-3 py-1 rounded text-primary font-semibold'>
                          {psp.folio}
                        </span>
                        <Badge variant='outline' className='bg-green-50 text-green-700 border-green-200'>
                          {psp.status}
                        </Badge>
                        {psp.area && psp.area !== 'General' && (
                          <Badge variant='outline' className='bg-purple-50 text-purple-700 border-purple-200'>
                            {psp.area}
                          </Badge>
                        )}
                        {psp.parent_psp_id && (
                          <Badge variant='outline' className='bg-amber-50 text-amber-700 border-amber-200'>
                            Fragmentada
                          </Badge>
                        )}
                        {psp.billing_status === 'pending_start' && (
                          <Badge variant='outline' className='bg-orange-50 text-orange-700 border-orange-300'>
                            <Clock className='w-3 h-3 mr-1' />
                            {psp.billing_start_mode === 'fixed_date'
                              ? `Cobro desde ${psp.billing_start_date ? new Date(psp.billing_start_date).toLocaleDateString('es-CL') : 'fecha definida'}`
                              : `Cobro al finalizar ${psp.related_psp_folio || 'otra PSP'}`
                            }
                          </Badge>
                        )}
                        {psp.honorario_resultado_texto && (
                          <Badge variant='outline' className='bg-emerald-50 text-emerald-700 border-emerald-300' data-testid={`badge-fee-resultado-${psp.folio}`}>
                            <AlertTriangle className='w-3 h-3 mr-1' />
                            Fee a Resultado
                          </Badge>
                        )}
                      </div>
                      <div className='flex items-center gap-4'>
                        <div className='flex gap-2'>
                          <Button
                            size='sm' variant='outline'
                            onClick={(e) => { e.stopPropagation(); handleViewPSP(psp.id, psp.folio); }}
                            title='Ver PSP'
                            className='flex items-center gap-1'
                          >
                            <Eye className='w-4 h-4' />
                            <span className='text-xs'>PSP</span>
                          </Button>
                          <Button
                            size='sm' variant='outline'
                            onClick={(e) => { e.stopPropagation(); handleViewCPS(psp.id, psp.folio); }}
                            title='Ver CPS'
                            className='flex items-center gap-1'
                          >
                            <Eye className='w-4 h-4' />
                            <span className='text-xs'>CPS</span>
                          </Button>
                        </div>
                        <div className='text-right'>
                          <p className='text-lg font-bold text-slate-900'>{psp.precio_uf?.toFixed(2)} UF</p>
                          <p className='text-xs text-slate-500'>
                            {psp.cuotas_pagadas}/{psp.total_cuotas} pagadas
                          </p>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedPSP === psp.id ? 'rotate-180' : ''}`} />
                      </div>
                    </div>

                    <p className='text-sm text-slate-600 p-4 pb-2 bg-white'>{psp.titulo}</p>

                    {expandedPSP === psp.id && (
                      <div className='bg-white px-4 pb-4'>
                        <div className='border border-slate-200 rounded-lg overflow-hidden'>
                          <div className='bg-slate-600 text-white px-4 py-2'>
                            <h5 className='text-sm font-semibold flex items-center gap-2'>
                              <Receipt className='w-4 h-4' />
                              Tabla de Devengo - Cuotas
                              <span className='text-xs font-normal text-slate-300'>({psp.folio})</span>
                            </h5>
                          </div>
                          <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                              <thead>
                                <tr className='bg-slate-100 border-b border-slate-200'>
                                  <th className='text-left p-3 font-semibold text-slate-700'>N° Cuota</th>
                                  <th className='text-right p-3 font-semibold text-slate-700'>Monto UF</th>
                                  <th className='text-right p-3 font-semibold text-slate-700'>Monto CLP</th>
                                  <th className='text-center p-3 font-semibold text-slate-700'>Fecha Estimada</th>
                                  <th className='text-center p-3 font-semibold text-slate-700'>Estado</th>
                                  <th className='text-right p-3 font-semibold text-slate-700'>Saldo Vencido</th>
                                </tr>
                              </thead>
                              <tbody>
                                {psp.cuotas.map((cuota, idx) => (
                                  <tr key={idx} className='border-b border-slate-100 hover:bg-slate-50'>
                                    <td className='p-3'>
                                      <span className='font-medium'>
                                        {cuota.es_cuota_exito ? '🏆 Éxito' : `Cuota ${cuota.numero}`}
                                      </span>
                                      {cuota.hito && (
                                        <p className='text-xs text-slate-500 mt-0.5'>{cuota.hito}</p>
                                      )}
                                    </td>
                                    <td className='p-3 text-right font-mono'>{cuota.monto_uf?.toFixed(2)} UF</td>
                                    <td className='p-3 text-right font-mono text-slate-600'>
                                      ${new Intl.NumberFormat('es-CL').format(cuota.monto_clp)}
                                    </td>
                                    <td className='p-3 text-center'>
                                      {cuota.es_cuota_exito ? (
                                        <span className='text-xs text-amber-600'>Al cumplir hito</span>
                                      ) : cuota.fecha_vencimiento ? (
                                        <span className='text-xs'>
                                          {new Date(cuota.fecha_vencimiento).toLocaleDateString('es-CL')}
                                        </span>
                                      ) : (
                                        <span className='text-xs text-slate-400'>A la firma</span>
                                      )}
                                    </td>
                                    <td className='p-3 text-center'>
                                      <Badge
                                        variant='outline'
                                        className={
                                          cuota.status === 'Pagada' ? 'bg-green-50 text-green-700 border-green-200' :
                                          cuota.status === 'Pago Parcial' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                          cuota.status === 'Facturada' ? (
                                            cuota.factura_info?.es_vencida
                                              ? 'bg-red-50 text-red-700 border-red-200'
                                              : 'bg-blue-50 text-blue-700 border-blue-200'
                                          ) :
                                          'bg-orange-50 text-orange-700 border-orange-200'
                                        }
                                      >
                                        {cuota.status === 'Facturada' ? (
                                          cuota.factura_info?.es_vencida
                                            ? `Facturada - Vencida ${cuota.factura_info.dias_vencida}d`
                                            : 'Facturada - Pago pendiente'
                                        ) : cuota.status}
                                      </Badge>
                                    </td>
                                    <td className='p-3 text-right'>
                                      {(cuota.status === 'Pago Parcial' || cuota.factura_info?.es_vencida) && cuota.factura_info?.pending_amount_clp ? (
                                        <span className='font-mono text-red-600 font-medium'>
                                          ${new Intl.NumberFormat('es-CL').format(cuota.factura_info.pending_amount_clp)}
                                        </span>
                                      ) : (
                                        <span className='text-slate-400'>-</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className='bg-slate-200 font-semibold'>
                                  <td className='p-3'>TOTAL</td>
                                  <td className='p-3 text-right font-mono'>{psp.precio_uf?.toFixed(2)} UF</td>
                                  <td className='p-3 text-right font-mono'>
                                    ${new Intl.NumberFormat('es-CL').format(psp.precio_total)}
                                  </td>
                                  <td colSpan={3} className='p-3 text-center text-slate-700'>
                                    Pendiente: {psp.monto_pendiente_uf?.toFixed(2)} UF |
                                    Pagado: {psp.monto_pagado_uf?.toFixed(2)} UF
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ));
        })()}
      </div>
    )}
  </div>
);

export default IngresosTab;
